import { MutationTree, ActionTree, GetterTree } from 'vuex'
import { 
  SET_CREDENTIALS_IS_LOADED, 
  SET_TAGS, 
  AUTH_GIFT_REDEEM, 
  SET_GOOGLE_TAG_MANAGER_IDS_IS_LOADED,
  SET_ACHIEVEMENT_GROUPS,
  SET_ADHOC_TICKET_CONDITION,
  SET_TICKET_LEGEND,
  SET_TRANSACTION_LIMIT,
  SET_TICKET_PRICE,
  SET_TAG,
  SET_IS_TRANSACTION_LIMIT_REACHED,
  SET_TRANSACTION_LIMIT_MESSAGE,
  SET_ACHIEVEMENT_MEDIAS,
  CLEAR_ACHIEVEMENT_MEDIAS,
  SET_ACKNOWLEDGMENT,
} from '../types'
import axios, { AxiosRequestConfig } from 'axios'
import store, { RootState } from '@/store'

import { getShopifyShop, initShopifyClient } from '@/utils/shopify'
import { get } from 'lodash-es'
import { API_URL, MERCHANT_API_KEY } from '@/config/constants'

export interface IRedemption {
  from: string;
  to: string;
}

export interface INotifications {
  redemption: IRedemption;
}

export interface IEvent {
  title: string;
  logo?: {
    default?: string;
    completed?: string;
  }
  endDate: string;
  startDate: string;
  isEnableRedemption: boolean;
}

export interface IAchievementGroup {
  _id: string;
  achievements: IAchievement[]
}

export interface IAchievementMedia {
  _id: string,
  date: string, 
  url: string,
  type: string,
}

export interface IAchievement {
  _id: string;
  catalogue: {
    shop: {
      name: string
    }
  };
  event?: IEvent;
  status: string;
  isRedeemed: boolean;
  hasAdditionalInfo: boolean
  createdAt: string;
}
export interface IAdhocTicketingCondition {
  isEnable: boolean,
  text: string,
}
export interface ITicketLegend {
  isEnable: boolean;
}

export interface ITransactionLimit {
  isEnable: boolean,
  maximum: number,
  frequency: string,
}
export interface ITicketPrice {
  isEnable: boolean;
}

export interface IDiscountPreview {
  isEnable: boolean;
}

export interface IStudentVerification {
  isEnable: boolean;
}

export interface IAcknowledgement {
  isEnable: boolean;
  acknowledgements: string[]
}

export interface ITicketEligibilityNotice {
  isEnable: boolean;
  text: string;
}

export interface ICulturePass {
  isEnable: boolean;
}

export interface ISellingFastIndicator {
  isEnable: boolean;
}

export interface ShopifyState {
  isLoaded: boolean;
  purchaseTimeDays: number;
  shopName: string;
  currency: string;
  storefrontAccessToken: string;
  domain: string;
  ticketTags: string;
  googleTagManagerIds: string[];
  isGoogleTagManagerIdsLoaded: boolean;
  adhocTicketingCondition?: IAdhocTicketingCondition;
  ticketLegend?: ITicketLegend;
  transactionLimit?: ITransactionLimit,
  transactionLimitMessage?: string,
  isTransactionLimitReached: boolean,
  ticketPrice?: ITicketPrice;
  apiKey: string;
  notifications?: INotifications;
  tag: string | null;
  achievementGroups?: IAchievementGroup[];
  achievementMedias: IAchievementMedia[];
  acknowledgement?: IAcknowledgement;
  discountPreview?: IDiscountPreview;
  studentVerification?: IStudentVerification;
  ticketEligibilityNotice?: ITicketEligibilityNotice;
  culturePass?: ICulturePass;
  sellingFastIndicator?: ISellingFastIndicator;
}

const state: ShopifyState = {
  isLoaded: false,
  purchaseTimeDays: 0,
  shopName: '',
  currency: 'SGD',
  storefrontAccessToken: '',
  domain: '',
  ticketTags: '',
  googleTagManagerIds: [],
  isGoogleTagManagerIdsLoaded: false,
  adhocTicketingCondition: undefined,
  ticketLegend: undefined,
  transactionLimit: undefined,
  transactionLimitMessage: undefined,
  isTransactionLimitReached: false,
  ticketPrice: undefined,
  apiKey: MERCHANT_API_KEY,
  notifications: undefined,
  tag: null,
  achievementGroups: [],
  achievementMedias: [],
  acknowledgement: undefined,
  discountPreview: undefined,
  studentVerification: undefined,
  ticketEligibilityNotice: undefined,
  culturePass: undefined,
  sellingFastIndicator: undefined,
}

const getters: GetterTree<ShopifyState, RootState> = {
  isCredentialsLoaded: (state) => state.isLoaded,
  purchaseTimeDays: (state) => state.purchaseTimeDays,
  shopName: (state) => state.shopName,
  currency: (state) => state.currency,
  storefrontAccessToken: (state) => state.storefrontAccessToken,
  domain: (state) => state.domain,
  ticketTags: (state) => state.ticketTags,
  googleTagManagerIds: (state) => state.googleTagManagerIds ?? [],
  isGoogleTagManagerIdsLoaded: (state) => state.isGoogleTagManagerIdsLoaded,
  isAdhocEnable: (state) => state.adhocTicketingCondition?.isEnable ?? false,
  customText: (state) => state.adhocTicketingCondition?.text ?? '',
  isTicketLegendEnable: (state) => state.ticketLegend?.isEnable ?? false,
  isTransactionLimitEnable: (state) => state.transactionLimit?.isEnable ?? false,
  isTransactionLimitReached: (state) => state.isTransactionLimitReached,
  transactionLimitMessage: (state) => state.transactionLimitMessage ?? '',
  isTicketPriceEnable: (state) => state.ticketPrice?.isEnable ?? false,
  apiKey: (state) => state.apiKey,
  notifications: (state) => state.notifications,
  tag: (state) => state.tag,
  achievementGroups: (state) => state.achievementGroups,
  achievementMedias: (state) => state.achievementMedias,
  acknowledgement: (state) => state.acknowledgement,
  isDiscountPreviewEnable: (state) => state.discountPreview?.isEnable ?? true,
  isStudentVerificationEnabled: (state) => state.studentVerification?.isEnable ?? false,
  ticketEligibilityNotice: (state) => state.ticketEligibilityNotice,
  isCulturePassEnable: (state) => state.culturePass?.isEnable ?? false,
  isSellingFastIndicatorEnable: (state) => state.sellingFastIndicator?.isEnable ?? false,
}

const mutations: MutationTree<ShopifyState> = {
  [SET_CREDENTIALS_IS_LOADED](state, { 
    isLoaded, 
    purchaseTimeDays, 
    shopName, 
    currency,
    storefrontAccessToken, 
    domain, 
    googleTagManagerIds, 
    adhocTicketingCondition, 
    ticketLegend,
    transactionLimit,
    ticketPrice,
    notifications,
    acknowledgement,
    discountPreview,
    studentVerification,
    ticketEligibilityNotice,
    culturePass,
    sellingFastIndicator,
  }) {
    state.isLoaded = isLoaded
    state.purchaseTimeDays = purchaseTimeDays
    state.shopName = shopName
    state.currency = currency || 'SGD'
    state.storefrontAccessToken = storefrontAccessToken
    state.domain = domain
    state.googleTagManagerIds = googleTagManagerIds
    state.adhocTicketingCondition = adhocTicketingCondition
    state.ticketLegend = ticketLegend
    state.transactionLimit = transactionLimit
    state.ticketPrice = ticketPrice
    state.notifications = notifications
    state.acknowledgement = acknowledgement
    state.discountPreview = discountPreview
    state.studentVerification = studentVerification
    state.ticketEligibilityNotice = ticketEligibilityNotice
    state.culturePass = culturePass
    state.sellingFastIndicator = sellingFastIndicator
  },
  [SET_GOOGLE_TAG_MANAGER_IDS_IS_LOADED](state) {
    state.isGoogleTagManagerIdsLoaded = true
  },
  [SET_ADHOC_TICKET_CONDITION](state, adhocTicketingCondition: IAdhocTicketingCondition) {
    state.adhocTicketingCondition = adhocTicketingCondition
  },
  [SET_TICKET_LEGEND](state, ticketLegend: ITicketLegend | undefined ) {
    state.ticketLegend = ticketLegend
  },
  [SET_TRANSACTION_LIMIT](state, transactionLimit: ITransactionLimit | undefined) {
    state.transactionLimit = transactionLimit
  },
  [SET_TRANSACTION_LIMIT_MESSAGE](state, transactionLimitMessage: string | undefined) {
    state.transactionLimitMessage = transactionLimitMessage
  },
  [SET_IS_TRANSACTION_LIMIT_REACHED](state, isTransactionLimitReached: boolean) {
    state.isTransactionLimitReached = isTransactionLimitReached
  },
  [SET_TICKET_PRICE](state, ticketPrice: ITicketPrice | undefined ) {
    state.ticketPrice = ticketPrice
  },
  [SET_TAGS](state, ticketTags) {
    state.ticketTags = ticketTags
  },
  setAPIKey(state, { apiKey }) {
    state.apiKey = apiKey
  },
  [SET_TAG](state, tag) {
    state.tag = tag
  },
  [SET_ACHIEVEMENT_GROUPS]( state, achievementGroups ) {
    state.achievementGroups = achievementGroups
  },
  [SET_ACHIEVEMENT_MEDIAS]( state, achievementMedias: IAchievementMedia[] ) {
    state.achievementMedias = [...state.achievementMedias, ...achievementMedias]
  },
  [CLEAR_ACHIEVEMENT_MEDIAS]( state ) {
    state.achievementMedias = []
  },
  [SET_ACKNOWLEDGMENT]( state, acknowledgement: IAcknowledgement ) {
    state.acknowledgement = acknowledgement
  }
}

const actions: ActionTree<ShopifyState, RootState> = {
  async loadCredentials({ commit, state }) {
    const res = await axios.get('/catalogues/shopify/init', {
      headers: {
        'api-key': state.apiKey
      }
    })
    const {
      config: { 
        domain, 
        currency,
        storefrontAccessToken, 
        purchaseTime, 
        googleTagManagerIds,
        adhocTicketingCondition,
        ticketLegend,
        transactionLimit,
        ticketPrice,
        notifications,
        acknowledgement,
        discountPreview,
        studentVerification,
        ticketEligibilityNotice,
        culturePass,
        sellingFastIndicator,
      },
      apiKey
    } = res.data
    initShopifyClient(storefrontAccessToken, domain)
    const shop = await getShopifyShop()
    commit(SET_CREDENTIALS_IS_LOADED, {
      isLoaded: true,
      purchaseTimeDays: +purchaseTime,
      shopName: shop?.name ?? '',
      currency: currency || 'SGD',
      storefrontAccessToken: storefrontAccessToken,
      domain: domain,
      googleTagManagerIds: googleTagManagerIds,
      adhocTicketingCondition: adhocTicketingCondition,
      ticketLegend: ticketLegend,
      transactionLimit: transactionLimit,
      ticketPrice: ticketPrice,
      notifications: notifications,
      apiKey,
      acknowledgement: acknowledgement,
      discountPreview: discountPreview,
      studentVerification: studentVerification,
      ticketEligibilityNotice: ticketEligibilityNotice,
      culturePass: culturePass,
      sellingFastIndicator: sellingFastIndicator,
    })
  },

  async getProductTags({ commit, getters }, id: string): Promise<Array<string>> {
    const data = JSON.stringify({
      query: `{
      node(id: "${id}") { 
        id
        ... on Product {
          tags
        }
      }
    }`,
      variables: {}
    })

    const config: AxiosRequestConfig = {
      method: 'post',
      url: `https://${getters.domain}/api/2023-07/graphql.json`,
      headers: {
        'X-Shopify-Storefront-Access-Token': getters.storefrontAccessToken,
        'Content-Type': 'application/json'
      },
      data: data
    }

    const res = await axios(config)

    commit(SET_TAGS, res.data.data?.node.tags)

    return res.data.data === undefined ? ['NO-RESULT'] : res.data.data.node === undefined ? [] : res.data.data.node.tags === undefined ? [] : res.data.data.node.tags
  },

  async refundOrder(checkoutId) {
    const data = JSON.stringify({
      checkoutId: checkoutId
    })
    const config: AxiosRequestConfig = {
      method: 'post',
      url: `${API_URL}/api/v1/shopify/refunds`,
      headers: {
        'x-ticket-token': store.getters.getToken,
        'Content-Type': 'application/json'
      },
      data: data
    }
    const res = await axios(config)
    return res.data
  },

  async giftRedeem({ commit }, payload: { email: string; isSubscribe: boolean }) {
    const data = JSON.stringify({
      email: payload.email,
      isSubscribe: payload.isSubscribe
    })
    const config: AxiosRequestConfig = {
      method: 'post',
      url: `${API_URL}/api/v1/shopify/subscriptions`,
      headers: {
        'x-ticket-token': store.getters.getToken,
        'Content-Type': 'application/json'
      },
      data: data
    }
    const res = await axios(config)
    commit(AUTH_GIFT_REDEEM, null, { root: true })
    return res.data
  },

  async checkExist({ commit }) {
    const config: AxiosRequestConfig = {
      method: 'get',
      url: `${API_URL}/api/v1/shopify/subscriptions/check-exist`,
      headers: {
        'x-ticket-token': store.getters.getToken,
        'Content-Type': 'application/json'
      },
    }
    const res = await axios(config)
    if(res.data === true) {
      commit(AUTH_GIFT_REDEEM, null, { root: true })
    }
    return res.data
  },

  async checkLimit({ commit, rootGetters }) {
    if (!rootGetters.isGuestLogin) {
      try {
        const config: AxiosRequestConfig = {
          method: 'get',
          url: `${API_URL}/api/v1/shopify/orders/check-limit`,
          headers: {
            'x-ticket-token': store.getters.getToken,
            'Content-Type': 'application/json'
          },
        }
  
        await axios(config)
      } catch (error) {
        const message = get(error, 'response.data.message')
        commit(SET_TRANSACTION_LIMIT_MESSAGE, message)
        commit(SET_IS_TRANSACTION_LIMIT_REACHED, true)
      }
    }
  },

  async loadAchievementGroups({ commit }) {
    const config: AxiosRequestConfig = {
      method: 'get',
      url: `${API_URL}/api/v1/shopify/achievements`,
      headers: {
        'x-ticket-token': store.getters.getToken,
        'Content-Type': 'application/json'
      },
    }

    const res = await axios(config)

    commit(SET_ACHIEVEMENT_GROUPS, res.data.data)
  },

  async loadAchievementMedias({ commit }, payload: { id: string, page?: number, limit?: number }) {
    const { id, page = 1, limit = 6 } = payload
    const config: AxiosRequestConfig = {
      method: 'get',
      url: `${API_URL}/api/v1/shopify/achievements/${id}/medias?page=${page}&limit=${limit}`,
      headers: {
        'x-ticket-token': store.getters.getToken,
        'Content-Type': 'application/json'
      },
    }

    const res = await axios(config)

    commit(SET_ACHIEVEMENT_MEDIAS, res.data.data)
  },

  clearAchievementMedias({ commit }) {
    commit(CLEAR_ACHIEVEMENT_MEDIAS)
  },

  async createWaitlist(_ctx, payload: {
    email: string;
    productId: string;
    productName: string;
    facilityId: string;
    slotId: string;
    facilityName: string;
    slotDate: string;
    slotStart: string;
    slotEnd: string;
  }): Promise<void> {
    const config: AxiosRequestConfig = {
      method: 'post',
      url: `${API_URL}/api/v1/shopify/waitlists`,
      headers: {
        'Content-Type': 'application/json',
        'api-key': store.getters.apiKey,
      },
      data: payload,
    }
    await axios(config)
  },
}

export default {
  state,
  getters,
  actions,
  mutations,
}
